import { useRef } from "react";
import { MondayStyledDropdownPicker } from "frontend/ui-components/picker/monday-styled-dropdown-picker/monday-styled-dropdown-picker";
import { useHover } from "frontend/ui-components/floaters/use-hover";
import { Tooltip } from "frontend/ui-components/floaters/tooltip";
import { useIsTextOverflowing } from "frontend/hooks/use-is-text-overflowing";
import styles from "./column-selector.module.css";
import { MondayColumn } from "shared/integrations/mapper";

interface ColumnSelectorProps {
  columns: MondayColumn[];
  selectedColumn: string | null;
  onSelect: (columnId: string) => void;
  placeholder: string;
  disabled?: boolean;
  isColumnDisabled?: (column: MondayColumn) => boolean;
  disabledColumnTooltip?: string;
  inlinePrefix?: string;
}

function ColumnOption({
  column,
  isDisabled,
  disabledTooltip,
}: {
  column: MondayColumn;
  isDisabled: boolean;
  disabledTooltip?: string;
}) {
  const nameRef = useRef<HTMLDivElement>(null);
  const nameHover = useHover(nameRef);
  const isOverflowing = useIsTextOverflowing(nameRef);

  const tooltipLabel = (() => {
    if (!nameHover) return null;

    if (isDisabled && disabledTooltip) {
      return disabledTooltip;
    }
    if (isOverflowing) {
      return column.title;
    }
    return null;
  })();

  return (
    <div className={isDisabled ? styles.resourceItemOptionDisabled : styles.resourceItemOption} ref={nameRef}>
      {column?.title || ""}
      {!!tooltipLabel && (
        <Tooltip
          label={tooltipLabel}
          relativeTo={nameRef}
          design="monday"
          side="right"
          customStyle={{ borderRadius: 3 }}
        />
      )}
    </div>
  );
}

export function ColumnSelector({
  columns,
  selectedColumn,
  onSelect,
  placeholder,
  disabled = false,
  isColumnDisabled,
  disabledColumnTooltip = "Column type not supported",
  inlinePrefix,
}: ColumnSelectorProps): React.ReactElement {
  const selectedColumnData = columns.find((col) => col.id === selectedColumn);

  const renderSelectedOption =
    inlinePrefix && selectedColumnData
      ? (option: MondayColumn | null) => {
          return (
            <div className={styles.selectedText}>
              <span className={styles.inlinePrefix}>{inlinePrefix}</span>
              {option?.title || ""}
            </div>
          );
        }
      : undefined;

  return (
    <MondayStyledDropdownPicker<MondayColumn>
      options={columns}
      selectedOption={selectedColumnData || null}
      onChange={(column) => onSelect(column.id)}
      isSelected={(col) => col.id === selectedColumn}
      getTitle={(option) => option.title || ""}
      renderOption={(option) => {
        const isDisabled = isColumnDisabled?.(option) ?? false;
        return <ColumnOption column={option} isDisabled={isDisabled} disabledTooltip={disabledColumnTooltip} />;
      }}
      renderSelectedOption={renderSelectedOption}
      placeholder={placeholder}
      disabled={disabled}
      width="158px"
      useFloatingDropdown={true}
      isOptionDisabled={isColumnDisabled}
      enableSearch={true}
      searchPlaceholder="Search..."
      trackingEventName="cim_column_selected"
    />
  );
}
